import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const GlyphContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.5rem;
  perspective: 1000px;
`;

const Glyph = styled(motion.div)`
  font-size: 2.5rem;
  color: #00ff88;
  text-shadow: 
    0 0 10px rgba(0, 255, 136, 0.5),
    0 0 20px rgba(0, 255, 136, 0.3);
  font-family: 'Arial Unicode MS', sans-serif;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -5px;
    border: 1px solid rgba(0, 255, 136, 0.3);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const playQuantumSound = (frequency = 440) => {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  // Create oscillator
  const oscillator = audioContext.createOscillator();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
  
  // Create gain node for volume control
  const gainNode = audioContext.createGain();
  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.05);
  gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);
  
  // Connect nodes
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Start and stop
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.3);
  
  // Cleanup
  setTimeout(() => {
    audioContext.close();
  }, 1000);
};

export const QuantumGlyphs: React.FC<{ glyphs: string[] }> = ({ glyphs }) => {
  const playGlyphSound = useCallback((index: number) => {
    // Different frequency for each glyph
    const baseFrequency = 440;
    const frequency = baseFrequency + (index * 220);
    playQuantumSound(frequency);
  }, []);

  return (
    <GlyphContainer>
      {glyphs.map((glyph, index) => (
        <Glyph
          key={index}
          initial={{ opacity: 0, scale: 0, rotateY: -180 }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            rotateY: 0,
          }}
          transition={{
            duration: 1,
            delay: index * 0.3,
            ease: "easeOut"
          }}
          whileHover={{ 
            scale: 1.2,
            rotateY: 360,
            transition: { duration: 0.8 }
          }}
          onAnimationComplete={() => playGlyphSound(index)}
        >
          {glyph}
        </Glyph>
      ))}
    </GlyphContainer>
  );
}; 